/**
 * Panel Redirect Component
 * Smart routing component that redirects users to their appropriate panel
 * based on roles and subscription tier
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { usePanelAccess } from '@/hooks/use-panel-access';
import { Loader2 } from 'lucide-react';

export function PanelRedirect() {
  const { user } = useFirebaseAuth();
  const {
    canAccessInstructorPanel,
    canAccessSellerPanel,
    canAccessProfessionalPanel,
    subscriptionTier,
  } = usePanelAccess();
  const [, navigate] = useLocation();
  
  useEffect(() => {
    if (!user) {
      // Not authenticated, redirect to login
      navigate('/auth');
      return;
    }

    // Panel Redirect Priority Logic:
    // 1. ADMIN role → User dashboard in this app (admin app is external)
    // 2. SELLER + seller tier → Seller Panel
    // 3. PROFESSIONAL + professional tier → Professional Panel
    // 4. INSTRUCTOR → Instructor Panel (last, see panel-route.tsx)
    // 5. FREE → Dashboard (default)
    
    let targetPanel = '/dashboard'; // default
    let panelName = 'Dashboard';
    
    if (user?.profile?.roles?.includes('ADMIN') || user?.roles?.includes?.('ADMIN')) {
      targetPanel = '/dashboard/user';
      panelName = 'Dashboard';
    } else if (canAccessSellerPanel) {
      targetPanel = '/seller';
      panelName = 'Seller Panel';
    } else if (canAccessProfessionalPanel) {
      targetPanel = '/professional';
      panelName = 'Professional Panel';
    } else if (canAccessInstructorPanel) {
      targetPanel = '/instructor';
      panelName = 'Instructor Panel';
    }
    
    console.log('🎯 PanelRedirect Logic:', {
      user: user?.email,
      tier: subscriptionTier || 'Free',
      roles: user?.profile?.roles || user?.roles || [],
      redirectTo: targetPanel,
      panelName,
      logic: {
        canAccessInstructorPanel,
        canAccessSellerPanel,
        canAccessProfessionalPanel,
      }
    });
    
    // Redirect to appropriate panel
    navigate(targetPanel);
    
  }, [user, canAccessInstructorPanel, canAccessSellerPanel, canAccessProfessionalPanel, navigate, subscriptionTier]);
  
  // Show loading screen while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-cyan-400" />
        <h2 className="text-xl font-semibold text-white mb-2">Redirecting to your panel...</h2>
        <p className="text-gray-400">
          Taking you to the right place based on your permissions
        </p>
      </div>
    </div>
  );
}
